/**
 * Utilities for selecting which documents should be shared after a property transfer.
 *
 * Purpose: Centralize the candidate selection logic used by the auto-share subscriber.
 */
import { isOnOrAfter } from '@/utils/dateUtils';
import { UploadedRecord } from '@/hooks/house/documents/useUploadedRecords.ts';
import { DocumentWithDownloadUrl as Document } from '@/client';

export type MinimalDoc = { id: number; created_at?: string | null };
export type MinimalRecord = { document?: MinimalDoc | null };

/**
 * Type guard for records that contain a document with a numeric id.
 */
export const hasDocumentWithId = (r: MinimalRecord): r is { document: MinimalDoc } => {
	return !!r?.document && typeof r.document.id === 'number';
};

/**
 * Selects documents whose upload timestamp is on or after the transfer timestamp.
 *
 * - Uses robust ISO normalization to avoid timezone/microseconds parsing issues
 * - Inclusive comparison (>=)
 */
export const selectPostTransferDocs = <T>(records: UploadedRecord[], transferAtIso: string | null): Document[] => {
	if (!records || !transferAtIso) return [];
	return records
		.filter(hasDocumentWithId)
		.map((r) => r.document)
		.filter((d) => isOnOrAfter(d.created_at ?? null, transferAtIso));
};
